import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { differenceInCalendarDays, parseISO, format, addMonths, startOfMonth, startOfWeek, addWeeks, addDays, getISOWeek } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { fmtDate, todayISO, addDaysISO } from "@/lib/format";
import { label } from "@/lib/labels";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronRight } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Scales & palette                                                    */
/* ------------------------------------------------------------------ */
export const ZOOM = {
  day: { px: 44, label: "Dag", short: "D" },
  week: { px: 14, label: "Uke", short: "U" },
  month: { px: 4.6, label: "Måned", short: "M" },
  quarter: { px: 1.9, label: "Kvartal", short: "K" },
};
export const ZOOM_ORDER = ["day", "week", "month", "quarter"];

/** Soft tinted fills with a solid colour edge – calm, readable, brand-first. */
export const GANTT_COLORS = {
  project: { bar: "bg-foreground/[0.07] text-foreground", edge: "bg-foreground/60" },
  prosjekt: { bar: "bg-blue-500/[0.13] text-blue-950 dark:bg-blue-400/[0.18] dark:text-blue-50", edge: "bg-blue-600 dark:bg-blue-400" },
  reservert: { bar: "bg-sky-400/[0.14] text-sky-950 dark:bg-sky-300/[0.16] dark:text-sky-50", edge: "bg-sky-500" },
  klargjoring: { bar: "bg-amber-400/[0.18] text-amber-950 dark:text-amber-50", edge: "bg-amber-500" },
  transport: { bar: "bg-orange-500/[0.14] text-orange-950 dark:text-orange-50", edge: "bg-orange-500" },
  montasje: { bar: "bg-teal-500/[0.14] text-teal-950 dark:text-teal-50", edge: "bg-teal-600" },
  demontasje: { bar: "bg-teal-700/[0.14] text-teal-950 dark:text-teal-50", edge: "bg-teal-800 dark:bg-teal-500" },
  service: { bar: "bg-amber-500/[0.16] text-amber-950 dark:text-amber-50", edge: "bg-amber-600" },
  inspeksjon: { bar: "bg-slate-400/[0.16] text-slate-900 dark:text-slate-50", edge: "bg-slate-500" },
  retur: { bar: "bg-slate-500/[0.14] text-slate-900 dark:text-slate-50", edge: "bg-slate-600" },
  annet: { bar: "bg-slate-400/[0.14] text-slate-900 dark:text-slate-50", edge: "bg-slate-500" },
  conflict: { bar: "bg-rose-500/[0.13] text-rose-950 dark:bg-rose-400/[0.18] dark:text-rose-50", edge: "bg-rose-600" },
};
export const LEGEND = [
  ["project", "Prosjektperiode"],
  ["prosjekt", "På prosjekt"],
  ["reservert", "Reservert"],
  ["klargjoring", "Klargjøring"],
  ["transport", "Transport"],
  ["montasje", "Montasje"],
  ["service", "Service"],
  ["retur", "Retur"],
  ["conflict", "Konflikt"],
];

export const utilizationTone = (pct) => (pct >= 100 ? "over" : pct >= 85 ? "high" : pct >= 60 ? "mid" : pct > 0 ? "low" : "none");
export const UTIL_CLASS = { none: "bg-border/60", low: "bg-primary/30", mid: "bg-primary/60", high: "bg-amber-400", over: "bg-rose-500" };
export const UTIL_LABEL = { none: "Ingen bruk", low: "Lav", mid: "Moderat", high: "Nær kapasitet", over: "Overbooket" };

const STATUS_DOT = { ledig: "bg-emerald-500", reservert: "bg-sky-500", klargjoring: "bg-amber-500", transport: "bg-orange-500", pa_prosjekt: "bg-blue-600", service: "bg-amber-600", retur: "bg-slate-500", ikke_tilgjengelig: "bg-rose-500" };
const ALLOC_TEXT = { prosjekt: "På prosjekt", reservert: "Reservert", klargjoring: "Klargjøring", transport: "Transport", service: "Service", retur: "Retur" };

const HEADER_MAJOR = 28;
const HEADER_MINOR = 20;
const STRIP_H = 8;
const DRAGGABLE = new Set(["allocation", "project"]);
const LOCKED_STATUS = new Set(["fullfort", "kansellert"]);
/* Infinite timeline: when the viewport gets within EDGE_PX of either end of the loaded window, ask the parent to extend it. */
const EDGE_PX = 240;
const EXTEND_DAYS = { day: 60, week: 120, month: 270, quarter: 540 };
const MAX_DAYS = 3650;

const isoOf = (d) => format(d, "yyyy-MM-dd");
const dayDiff = (a, b) => differenceInCalendarDays(parseISO(a), parseISO(b));

/** Assign vertical lanes so overlapping bars (conflicts) stack instead of hiding each other. */
function assignLanes(bars) {
  const sorted = [...bars].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  const laneEnds = [];
  const out = sorted.map((b) => {
    let lane = laneEnds.findIndex((end) => end <= b.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(b.end);
    } else laneEnds[lane] = b.end;
    return { ...b, lane };
  });
  return { bars: out, lanes: Math.max(1, laneEnds.length) };
}

export const barColor = (bar) => (bar.kind === "project" ? GANTT_COLORS.project : bar.conflict ? GANTT_COLORS.conflict : GANTT_COLORS[bar.type] || GANTT_COLORS.annet);

/** Small legend swatch (soft fill + edge). */
export const Swatch = ({ k, className }) => {
  const c = GANTT_COLORS[k] || GANTT_COLORS.annet;
  return (
    <span className={cn("relative inline-block h-3 w-5 overflow-hidden rounded-[3px]", c.bar, className)} aria-hidden>
      <span className={cn("absolute inset-y-0 left-0 w-[2px]", c.edge)} />
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Skeleton                                                             */
/* ------------------------------------------------------------------ */
export const GanttSkeleton = ({ rows = 9, leftWidth = 272 }) => (
  <div className="h-full w-full overflow-hidden" data-testid="planning-gantt-skeleton">
    <div className="flex border-b" style={{ height: HEADER_MAJOR + HEADER_MINOR + STRIP_H }}>
      <div className="border-r px-3 flex items-end pb-2" style={{ width: leftWidth }}><Skeleton className="h-3 w-24" /></div>
      <div className="flex-1 flex items-end gap-6 px-4 pb-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-3 w-16" />)}</div>
    </div>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex border-b border-border/60" style={{ height: 44 }}>
        <div className="border-r px-3 flex flex-col justify-center gap-1.5" style={{ width: leftWidth }}><Skeleton className="h-3 w-40" /><Skeleton className="h-2.5 w-24" /></div>
        <div className="flex-1 relative"><Skeleton className="absolute top-2.5 h-6 rounded-md" style={{ left: 40 + ((i * 137) % 400), width: 120 + ((i * 89) % 260) }} /></div>
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Gantt                                                                */
/* ------------------------------------------------------------------ */
/**
 * High-density planning Gantt.
 * - Single scroll container; sticky header + sticky (opaque) left column.
 * - Infinite in time: scrolling to either edge extends the loaded window (onRangeChange) without visual jumps.
 * - Drag to move, drag edges to resize (allocation + project bars) with ghost preview.
 * - Drag on empty row space to create a reservation.
 * - Lane stacking for overlapping bars (conflicts stay visible).
 * - Keyboard: arrows move selected bar (shift = 7 days), Enter opens, Esc deselects.
 */
export const Gantt = memo(forwardRef(function Gantt(
  { rows, start, end, zoom, view, focus, canWrite, selectedId, selectedRowId, onSelect, onOpen, onToggle, onRowClick, onBarChange, onCreate, onRangeChange, onVisibleRange, capacityTotals, renderRowActions, leftHeader, leftWidth = 272, className, emptyContent },
  ref
) {
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const createRef = useRef(null);
  const centerRef = useRef(null);
  const didInit = useRef(false);
  const prevRangeRef = useRef({ start, end });
  const pendingLeftRef = useRef(null); // scrollLeft to apply (in the NEW coordinate space) once the range has been extended
  const extendingRef = useRef(false);
  const rafRef = useRef(null);
  const lastVisibleRef = useRef("");
  const [drag, setDrag] = useState(null);
  const [pending, setPending] = useState({});
  const [create, setCreate] = useState(null);

  const pxPerDay = ZOOM[zoom]?.px || ZOOM.week.px;
  const totalDays = Math.max(1, dayDiff(end, start));
  const width = Math.round(totalDays * pxPerDay);
  const x = useCallback((d) => dayDiff(d, start) * pxPerDay, [start, pxPerDay]);
  const clampX = useCallback((v) => Math.min(width, Math.max(0, v)), [width]);
  const today = todayISO();
  const todayX = x(today);
  const headerH = HEADER_MAJOR + HEADER_MINOR + (capacityTotals ? STRIP_H : 0);
  const extDays = EXTEND_DAYS[zoom] || EXTEND_DAYS.week;

  /* ---- header scale ---- */
  const header = useMemo(() => {
    const s = parseISO(start);
    const e = parseISO(end);
    const major = [];
    let m = startOfMonth(s);
    while (m < e) {
      const next = addMonths(m, 1);
      const from = m < s ? s : m;
      const to = next > e ? e : next;
      const w = differenceInCalendarDays(to, from) * pxPerDay;
      major.push({ key: isoOf(m), label: format(m, w > 90 ? "MMMM yyyy" : w > 40 ? "MMM yy" : "MMM", { locale: nb }), left: x(isoOf(from)), width: w });
      m = next;
    }
    const minor = [];
    if (zoom === "day") {
      for (let d = s; d < e; d = addDays(d, 1)) minor.push({ key: isoOf(d), label: format(d, "d"), sub: format(d, "EEEEE", { locale: nb }), left: x(isoOf(d)), width: pxPerDay, weekend: [0, 6].includes(d.getDay()), today: isoOf(d) === today });
    } else if (zoom === "week" || zoom === "month") {
      for (let w = startOfWeek(s, { weekStartsOn: 1 }); w < e; w = addWeeks(w, 1)) {
        const from = w < s ? s : w;
        const to = addWeeks(w, 1) > e ? e : addWeeks(w, 1);
        minor.push({ key: isoOf(w), label: `${zoom === "week" ? "U" : ""}${getISOWeek(w)}`, left: x(isoOf(from)), width: differenceInCalendarDays(to, from) * pxPerDay, today: isoOf(w) <= today && today < isoOf(addWeeks(w, 1)) });
      }
    } else {
      for (const mm of major) minor.push({ key: mm.key, label: format(parseISO(mm.key), "MMM", { locale: nb }), left: mm.left, width: mm.width });
    }
    return { major, minor };
  }, [start, end, zoom, pxPerDay, x, today]);

  /* ---- grid background (uniform units via repeating gradients) ---- */
  const gridStyle = useMemo(() => {
    const wd = (parseISO(start).getDay() + 6) % 7; // Monday = 0
    const layers = [];
    const sizes = [];
    const positions = [];
    if (zoom === "day") {
      layers.push(`repeating-linear-gradient(to right, transparent 0 ${5 * pxPerDay}px, hsl(var(--muted) / 0.6) ${5 * pxPerDay}px ${7 * pxPerDay}px)`);
      sizes.push(`${7 * pxPerDay}px 100%`);
      positions.push(`${-wd * pxPerDay}px 0`);
      layers.push(`repeating-linear-gradient(to right, hsl(var(--border) / 0.45) 0 1px, transparent 1px ${pxPerDay}px)`);
      sizes.push(`${pxPerDay}px 100%`);
      positions.push("0 0");
    } else if (zoom === "week" || zoom === "month") {
      layers.push(`repeating-linear-gradient(to right, hsl(var(--border) / ${zoom === "week" ? 0.5 : 0.35}) 0 1px, transparent 1px ${7 * pxPerDay}px)`);
      sizes.push(`${7 * pxPerDay}px 100%`);
      positions.push(`${-wd * pxPerDay}px 0`);
    }
    return { backgroundImage: layers.join(","), backgroundSize: sizes.join(","), backgroundPosition: positions.join(","), backgroundRepeat: "repeat" };
  }, [start, zoom, pxPerDay]);

  /* ---- lanes per row ---- */
  const laid = useMemo(() => rows.map((r) => ({ row: r, ...assignLanes(r.bars || []) })), [rows]);
  const barIndex = useMemo(() => {
    const m = new Map();
    for (const { row, bars } of laid) for (const b of bars) m.set(b.id, { bar: b, row });
    return m;
  }, [laid]);
  /* row geometry (also used to keep the vertical scroll anchored when rows get inserted above the viewport) */
  const rowGeom = (row, lanes) => {
    const base = row.depth ? 36 : 44;
    const barH = row.depth ? 22 : 26;
    return { base, barH, rowH: base + (lanes - 1) * (barH + 4) };
  };
  const rowTopsRef = useRef([]);
  rowTopsRef.current = (() => {
    let t = 0;
    return laid.map(({ row, lanes }) => {
      const { rowH } = rowGeom(row, lanes);
      const o = { id: row.id, top: t, h: rowH };
      t += rowH;
      return o;
    });
  })();
  const anchorRef = useRef(null);

  /* ---- scrolling ---- */
  const visibleW = () => (scrollRef.current ? scrollRef.current.clientWidth - leftWidth : 800);
  const canExtend = (days) => totalDays + days <= MAX_DAYS;

  /** Ask the parent to extend the loaded window. `left` = scrollLeft to apply in the new coordinate space (optional). */
  const extend = useCallback((patch, left = null) => {
    if (!onRangeChange || extendingRef.current) return false;
    extendingRef.current = true;
    pendingLeftRef.current = left;
    onRangeChange(patch);
    return true;
  }, [onRangeChange]);

  const emitVisible = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onVisibleRange) return;
    const vs = addDaysISO(start, Math.floor(el.scrollLeft / pxPerDay));
    const ve = addDaysISO(start, Math.ceil((el.scrollLeft + Math.max(0, el.clientWidth - leftWidth)) / pxPerDay));
    const key = `${vs}|${ve}`;
    if (lastVisibleRef.current !== key) {
      lastVisibleRef.current = key;
      onVisibleRange({ start: vs, end: ve });
    }
  }, [start, pxPerDay, leftWidth, onVisibleRange]);

  const scrollToDate = useCallback((d, behavior = "smooth", opts = {}) => {
    const el = scrollRef.current;
    if (!el) return;
    const vw = visibleW();
    const target = (px) => Math.max(0, px - vw / 3);
    const px = x(d);
    if (onRangeChange) {
      // land with a margin to the edges of the loaded window, otherwise extend first (instant, no smooth-scroll fights)
      if (target(px) < EDGE_PX) {
        const ext = Math.max(extDays, dayDiff(start, d) + 30, Math.ceil((EDGE_PX + vw / 3 - px) / pxPerDay));
        if (extend({ start: addDaysISO(start, -ext) }, target(px + ext * pxPerDay))) return;
      } else if (px + (vw * 2) / 3 > width - EDGE_PX) {
        const ext = Math.max(extDays, dayDiff(d, end) + 30);
        if (extend({ end: addDaysISO(end, ext) }, target(px))) return;
      }
    }
    const left = target(px);
    el.scrollTo({ left, behavior: Math.abs(el.scrollLeft - left) > vw * 3 ? "auto" : behavior });
    if (opts.reveal) {
      // if none of the rows currently in view has anything in the target period, scroll vertically to the first row that does
      const vs = addDaysISO(start, Math.floor(left / pxPerDay));
      const ve = addDaysISO(start, Math.ceil((left + vw) / pxPerDay));
      const hasBar = ({ bars }) => bars.some((b) => b.start < ve && b.end > vs);
      const tops = rowTopsRef.current;
      const st = el.scrollTop;
      const vh = el.clientHeight - headerH;
      const inView = laid.filter((_, i) => tops[i] && tops[i].top + tops[i].h > st && tops[i].top < st + vh);
      if (!inView.some(hasBar)) {
        const idx = laid.findIndex(hasBar);
        if (idx >= 0) el.scrollTop = tops[idx].top;
      }
    }
  }, [x, start, end, extDays, pxPerDay, width, extend, onRangeChange, laid, headerH]);

  const scrollByPage = useCallback((dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const vw = visibleW();
    const target = el.scrollLeft + dir * vw * 0.8;
    if (target < EDGE_PX && canExtend(extDays) && extend({ start: addDaysISO(start, -extDays) }, Math.max(0, target + extDays * pxPerDay))) return;
    if (target + vw > width - EDGE_PX && canExtend(extDays) && extend({ end: addDaysISO(end, extDays) }, target)) return;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [start, end, extDays, pxPerDay, width, extend]);

  useImperativeHandle(ref, () => ({
    scrollToDate,
    scrollToToday: () => scrollToDate(today, "smooth", { reveal: true }),
    scrollByPage,
    scrollToRow: (rowId) => {
      const el = scrollRef.current?.querySelector(`[data-row-id="${rowId}"]`);
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
    },
  }));

  /* keep the same dates in view when the zoom changes */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (centerRef.current != null) el.scrollLeft = Math.max(0, centerRef.current * pxPerDay - visibleW() / 2);
  }, [pxPerDay]);

  /* range extended (or replaced): compensate scrollLeft so the visible dates do not move */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const prev = prevRangeRef.current;
    if (el && (prev.start !== start || prev.end !== end)) {
      if (pendingLeftRef.current != null) el.scrollLeft = pendingLeftRef.current;
      else if (prev.start !== start) el.scrollLeft += dayDiff(prev.start, start) * pxPerDay;
      pendingLeftRef.current = null;
      el.style.setProperty("--sl", `${el.scrollLeft}px`);
      centerRef.current = (el.scrollLeft + visibleW() / 2) / pxPerDay;
    }
    prevRangeRef.current = { start, end };
    extendingRef.current = false;
    lastVisibleRef.current = "";
    emitVisible();
  }, [start, end, pxPerDay, emitVisible]);

  /* the loaded window must always be wider than the viewport, otherwise there is nothing to scroll (and no scroll events to extend on) */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !onRangeChange) return;
    const need = Math.ceil(((el.clientWidth - leftWidth) * 2) / pxPerDay);
    if (totalDays < need && canExtend(need - totalDays)) {
      const add = Math.ceil((need - totalDays) / 2);
      extend({ start: addDaysISO(start, -add), end: addDaysISO(end, add) });
    }
  }, [pxPerDay, totalDays, leftWidth, start, end, onRangeChange, extend]);

  useEffect(() => {
    if (!didInit.current && rows.length && scrollRef.current) {
      didInit.current = true;
      scrollToDate(today, "auto");
      emitVisible();
    }
  }, [rows.length, scrollToDate, today, emitVisible]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  /* rows inserted above the viewport (e.g. older projects loaded while scrolling back in time) must not push the visible rows down */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const a = anchorRef.current;
    if (!el || !a) return;
    const r = rowTopsRef.current.find((t) => t.id === a.id);
    if (!r) return;
    const want = r.top - a.delta;
    if (Math.abs(want - el.scrollTop) > 1) el.scrollTop = want;
  }, [laid]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    centerRef.current = (el.scrollLeft + visibleW() / 2) / pxPerDay;
    el.style.setProperty("--sl", `${el.scrollLeft}px`);
    const first = rowTopsRef.current.find((t) => t.top + t.h > el.scrollTop);
    anchorRef.current = first ? { id: first.id, delta: first.top - el.scrollTop } : null;
    if (onRangeChange && !extendingRef.current && canExtend(extDays)) {
      if (el.scrollLeft < EDGE_PX) extend({ start: addDaysISO(start, -extDays) });
      else if (el.scrollLeft + el.clientWidth > el.scrollWidth - EDGE_PX) extend({ end: addDaysISO(end, extDays) });
    }
    if (onVisibleRange && rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        emitVisible();
      });
    }
  };

  /* ---- bar drag / resize ---- */
  const isDraggable = (bar) => canWrite && DRAGGABLE.has(bar.kind) && !LOCKED_STATUS.has(bar.status) && !bar.other;
  const onBarPointerDown = (e, bar, row) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const mode = e.target?.dataset?.handle || "move";
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { bar, row, mode: isDraggable(bar) ? mode : "none", startX: e.clientX, moved: false, origStart: bar.start, origEnd: bar.end, curStart: bar.start, curEnd: bar.end };
    setDrag({ barId: bar.id, mode, curStart: bar.start, curEnd: bar.end, moved: false });
  };
  const onBarPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) < 4) return;
    if (d.mode === "none") return;
    d.moved = true;
    const dDays = Math.round(dx / pxPerDay);
    let s = d.origStart;
    let en = d.origEnd;
    if (d.mode === "move") {
      s = addDaysISO(d.origStart, dDays);
      en = addDaysISO(d.origEnd, dDays);
    } else if (d.mode === "start") {
      s = addDaysISO(d.origStart, dDays);
      if (s >= en) s = addDaysISO(en, -1);
    } else {
      en = addDaysISO(d.origEnd, dDays);
      if (en <= s) en = addDaysISO(s, 1);
    }
    d.curStart = s;
    d.curEnd = en;
    setDrag({ barId: d.bar.id, mode: d.mode, curStart: s, curEnd: en, moved: true });
  };
  const onBarPointerUp = async () => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDrag(null);
    if (!d.moved) {
      onSelect?.(d.bar, d.row);
      return;
    }
    if (d.curStart === d.origStart && d.curEnd === d.origEnd) return;
    setPending((p) => ({ ...p, [d.bar.id]: { start: d.curStart, end: d.curEnd } }));
    try {
      await onBarChange?.(d.bar, d.row, { start: d.curStart, end: d.curEnd });
    } finally {
      setPending((p) => {
        const n = { ...p };
        delete n[d.bar.id];
        return n;
      });
    }
  };

  /* ---- drag-to-create on empty row space ---- */
  const dateAtX = (px, mode) => addDaysISO(start, mode === "floor" ? Math.floor(px / pxPerDay) : Math.ceil(px / pxPerDay));
  const canCreateOn = (row) => canWrite && onCreate && (row.kind === "module" || (row.kind === "project" && row.depth === 0));
  const onRowPointerDown = (e, row) => {
    if (e.button !== 0) return;
    if (!canCreateOn(row)) {
      onSelect?.(null, null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    createRef.current = { row, x0: px, x1: px, moved: false, rectLeft: rect.left };
    setCreate({ rowId: row.id, x0: px, x1: px, moved: false });
  };
  const onRowPointerMove = (e) => {
    const c = createRef.current;
    if (!c) return;
    c.x1 = Math.max(0, Math.min(width, e.clientX - c.rectLeft));
    if (!c.moved && Math.abs(c.x1 - c.x0) < 4) return;
    c.moved = true;
    setCreate({ rowId: c.row.id, x0: c.x0, x1: c.x1, moved: true });
  };
  const onRowPointerUp = () => {
    const c = createRef.current;
    createRef.current = null;
    setCreate(null);
    if (!c) return;
    if (!c.moved) {
      onSelect?.(null, null);
      return;
    }
    const a = Math.min(c.x0, c.x1);
    const b = Math.max(c.x0, c.x1);
    const s = dateAtX(a, "floor");
    let en = dateAtX(b, "ceil");
    if (en <= s) en = addDaysISO(s, 1);
    onCreate?.(c.row, { start: s, end: en });
  };

  /* ---- keyboard ---- */
  const onKeyDown = (e) => {
    if (!selectedId) return;
    const hit = barIndex.get(selectedId);
    if (!hit) return;
    const { bar, row } = hit;
    if (e.key === "Escape") {
      onSelect?.(null, null);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onOpen?.(bar, row);
      return;
    }
    if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && isDraggable(bar) && !pending[bar.id]) {
      e.preventDefault();
      const delta = (e.key === "ArrowLeft" ? -1 : 1) * (e.shiftKey ? 7 : 1);
      const next = { start: addDaysISO(bar.start, delta), end: addDaysISO(bar.end, delta) };
      setPending((p) => ({ ...p, [bar.id]: next }));
      Promise.resolve(onBarChange?.(bar, row, next)).finally(() =>
        setPending((p) => {
          const n = { ...p };
          delete n[bar.id];
          return n;
        })
      );
    }
  };

  const strip = capacityTotals || null;

  return (
    <TooltipProvider delayDuration={250}>
      <div
        ref={scrollRef}
        tabIndex={0}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        className={cn("relative h-full w-full overflow-auto scrollbar-thin outline-none", className)}
        data-testid="planning-gantt"
        aria-label="Planleggingstidslinje"
      >
        <div style={{ width: leftWidth + width, minWidth: "100%" }} className="relative flex min-h-full flex-col">
          {/* ---------- header ---------- */}
          <div className="sticky top-0 z-30 flex shrink-0" style={{ height: headerH }}>
            <div className="sticky left-0 z-40 shrink-0 border-r border-b bg-card flex items-end px-3 pb-1.5" style={{ width: leftWidth }}>
              {leftHeader}
            </div>
            <div className="relative shrink-0 border-b bg-card" style={{ width }}>
              {header.major.map((m) => (
                <div key={m.key} className="absolute top-0 flex items-center border-l border-border/70 px-2 text-[12px] font-medium text-foreground/85 truncate first-letter:uppercase" style={{ left: m.left, width: m.width, height: HEADER_MAJOR }}>
                  {m.width > 26 ? m.label : ""}
                </div>
              ))}
              {header.minor.map((m) => (
                <div
                  key={m.key}
                  className={cn("absolute flex items-center justify-center gap-0.5 border-l border-border/40 text-[10.5px] tabular text-muted-foreground/80", m.weekend && "bg-muted/60", m.today && "text-primary font-semibold")}
                  style={{ top: HEADER_MAJOR, left: m.left, width: m.width, height: HEADER_MINOR }}
                >
                  {m.width > 16 ? m.label : ""}
                  {m.sub && m.width > 34 && <span className="text-[9px] opacity-70">{m.sub}</span>}
                </div>
              ))}
              {strip && (
                <div className="absolute inset-x-0" style={{ top: HEADER_MAJOR + HEADER_MINOR, height: STRIP_H }} data-testid="planning-capacity-strip">
                  {strip.map((c) => {
                    const l = clampX(x(c.start));
                    const r = clampX(x(c.end));
                    if (r - l <= 0) return null;
                    const tone = utilizationTone(c.pct);
                    return (
                      <Tooltip key={c.key}>
                        <TooltipTrigger asChild>
                          <div className={cn("absolute rounded-[2px] transition-opacity duration-150 hover:opacity-70", UTIL_CLASS[tone])} style={{ left: l + 1, width: Math.max(2, r - l - 2), top: 1, height: STRIP_H - 4 }} data-testid={`planning-capacity-cell-${c.key}`} />
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-xs">
                          <div className="font-medium">Uke {c.label.replace(/^U/, "")} · {fmtDate(c.start, "d. MMM")}</div>
                          <div className="tabular">{c.pct} % utnyttelse · {c.busy} i bruk · {c.available} ledige</div>
                          <div className="text-muted-foreground">{UTIL_LABEL[tone]}</div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              )}
              {todayX >= 0 && todayX <= width && (
                <div className="absolute top-0 z-10 pointer-events-none" style={{ left: todayX, height: headerH }}>
                  <span className="absolute top-[7px] left-1.5 whitespace-nowrap text-[10px] font-semibold text-primary" data-testid="planning-today-pill">I dag</span>
                  <span className="absolute inset-y-0 -left-px w-px bg-primary/70" />
                </div>
              )}
            </div>
          </div>

          {/* ---------- body ---------- */}
          <div className="relative flex flex-1 flex-col">
            {/* continuous canvas grid */}
            <div className="absolute inset-y-0 pointer-events-none" style={{ left: leftWidth, width, ...gridStyle }} aria-hidden>
              {header.major.map((m) => <div key={m.key} className="absolute inset-y-0 border-l border-border/70" style={{ left: m.left }} />)}
              {todayX >= 0 && todayX <= width && zoom === "day" && <div className="absolute inset-y-0 bg-primary/[0.05]" style={{ left: todayX, width: pxPerDay }} />}
            </div>
            {rows.length === 0 ? (
              <div className="sticky left-0 z-[2] flex items-center justify-center py-16" style={{ width: "100%" }}>
                {emptyContent}
              </div>
            ) : (
              <div className="relative z-[2]">
                {laid.map(({ row, bars, lanes }) => {
                  const { base, barH, rowH } = rowGeom(row, lanes);
                  const isSelRow = selectedRowId === row.id;
                  const creating = create && create.rowId === row.id && create.moved;
                  return (
                    <div key={`${row.kind}-${row.id}`} className="group flex" style={{ height: rowH }} data-row-id={row.id} data-testid={`planning-gantt-row-${row.id}`}>
                      {/* left cell (opaque) */}
                      <div className="sticky left-0 z-20 shrink-0 flex items-center gap-1 border-r border-b border-border/60 bg-card text-sm" style={{ width: leftWidth, paddingLeft: 8 + row.depth * 18, paddingRight: 8 }}>
                        <div className={cn("pointer-events-none absolute inset-0 transition-colors duration-150", isSelRow ? "bg-primary/[0.06]" : "group-hover:bg-foreground/[0.025]")} aria-hidden />
                        {row.expandable ? (
                          <button type="button" onClick={() => onToggle?.(row.id)} className="relative h-6 w-6 shrink-0 inline-flex items-center justify-center rounded text-muted-foreground/70 hover:bg-muted hover:text-foreground" aria-label={row.expanded ? "Skjul detaljer" : "Vis detaljer"} aria-expanded={!!row.expanded} data-testid={`planning-row-expand-${row.id}`}>
                            {row.expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        ) : (
                          <span className={cn("shrink-0", row.depth ? "w-0" : "w-6")} />
                        )}
                        <button type="button" className="relative min-w-0 flex-1 text-left rounded px-1 py-0.5 focus-visible:ring-1 focus-visible:ring-ring" onClick={() => onRowClick?.(row)} onDoubleClick={() => onOpen?.(row.bars?.[0], row)} data-testid={`planning-row-label-${row.id}`}>
                          <div className="flex items-center gap-1.5 min-w-0 leading-tight">
                            {row.kind === "module" && row.status && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STATUS_DOT[row.status] || "bg-slate-400")} aria-hidden />}
                            <span className={cn("truncate", row.kind === "module" || row.kind === "allocation" ? "font-mono text-[12px]" : "text-[12.5px] font-medium", row.kind === "group" && "text-[12px] font-medium text-muted-foreground")}>{row.label}</span>
                            {row.has_conflict && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" title="Dobbelbooking" data-testid="planning-conflict-indicator" />}
                          </div>
                          {row.sublabel && <div className="truncate text-[11px] leading-tight text-muted-foreground/90">{row.sublabel}{row.kind === "module" && row.status ? ` · ${label(row.status)}` : ""}</div>}
                        </button>
                        {row.kind === "project" && row.module_count != null && <span className="relative shrink-0 font-mono text-[11px] tabular text-muted-foreground/70" title="Moduler">{row.module_count}</span>}
                        {renderRowActions && <div className="relative shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150">{renderRowActions(row)}</div>}
                      </div>

                      {/* timeline cell */}
                      <div
                        className={cn("relative shrink-0 border-b border-border/60 transition-colors duration-150", isSelRow ? "bg-primary/[0.04]" : "group-hover:bg-foreground/[0.02]", canCreateOn(row) && "cursor-crosshair")}
                        style={{ width }}
                        onPointerDown={(e) => onRowPointerDown(e, row)}
                        onPointerMove={onRowPointerMove}
                        onPointerUp={onRowPointerUp}
                        onPointerCancel={onRowPointerUp}
                      >
                        {bars.map((bar) => {
                          const dragging = drag?.barId === bar.id && drag.moved;
                          const eff = pending[bar.id] || (dragging ? { start: drag.curStart, end: drag.curEnd } : null);
                          const s = eff?.start ?? bar.start;
                          const en = eff?.end ?? bar.end;
                          const left = x(s);
                          const right = x(en);
                          if (!dragging && (right <= 0 || left >= width)) return null;
                          const l = clampX(left);
                          const r = clampX(right);
                          const w = Math.max(6, r - l);
                          const top = (base - barH) / 2 + bar.lane * (barH + 4);
                          const draggable = isDraggable(bar);
                          const selected = selectedId === bar.id;
                          const color = barColor(bar);
                          const text = bar.other ? `${bar.project_number || ""} ${bar.label}`.trim() : bar.kind === "allocation" && (view === "project" || focus) ? `${ALLOC_TEXT[bar.type] || label(bar.type)}${bar.status === "planlagt" ? " · planlagt" : ""}` : bar.label;
                          const days = Math.max(1, dayDiff(en, s));
                          const milestone = bar.kind !== "allocation" && bar.kind !== "project" && dayDiff(bar.end, bar.start) <= 1;
                          const wEff = milestone ? Math.max(w, 18) : w;
                          const showDays = !milestone && w > 150 && bar.kind !== "project";
                          return (
                            <div key={bar.id}>
                              {dragging && <div className={cn("absolute rounded-md opacity-40 pointer-events-none", color.bar)} style={{ left: clampX(x(bar.start)), width: Math.max(6, clampX(x(bar.end)) - clampX(x(bar.start))), top, height: barH }} aria-hidden />}
                              <Tooltip open={dragging ? false : undefined}>
                                <TooltipTrigger asChild>
                                  <div
                                    role="button"
                                    tabIndex={-1}
                                    aria-label={`${text} ${fmtDate(s)} – ${fmtDate(en)}`}
                                    className={cn(
                                      "absolute flex items-center overflow-hidden rounded-md text-[11.5px] font-medium select-none touch-none transition-[box-shadow,opacity] duration-150",
                                      color.bar,
                                      bar.status === "planlagt" && "bar-planned",
                                      bar.other && "bar-other",
                                      milestone && "rounded-full justify-center",
                                      draggable ? "cursor-grab active:cursor-grabbing hover:shadow-[0_1px_3px_rgba(16,24,40,0.12)]" : "cursor-pointer",
                                      LOCKED_STATUS.has(bar.status) && "opacity-60",
                                      selected && "ring-1 ring-primary shadow-[0_2px_8px_rgba(16,24,40,0.14)] z-20",
                                      dragging && "z-30 ring-1 ring-primary shadow-pop",
                                      pending[bar.id] && !dragging && "opacity-70 animate-pulse"
                                    )}
                                    style={{ left: l, width: wEff, top, height: barH }}
                                    onPointerDown={(e) => onBarPointerDown(e, bar, row)}
                                    onPointerMove={onBarPointerMove}
                                    onPointerUp={onBarPointerUp}
                                    onPointerCancel={onBarPointerUp}
                                    onDoubleClick={(e) => { e.stopPropagation(); onOpen?.(bar, row); }}
                                    data-testid={`planning-gantt-bar-${bar.id}`}
                                    data-kind={bar.kind}
                                  >
                                    {!milestone && <span className={cn("absolute inset-y-0 left-0 w-[3px]", color.edge)} aria-hidden />}
                                    {draggable && w > 30 && <span data-handle="start" className="absolute left-0 top-0 h-full w-2 cursor-ew-resize" />}
                                    {milestone ? (
                                      <span className={cn("h-1.5 w-1.5 rounded-full", color.edge)} aria-hidden />
                                    ) : (
                                      <span
                                        className="flex min-w-0 items-center gap-1.5 pl-2.5 pr-2 will-change-transform"
                                        style={w > 46 ? { transform: `translateX(clamp(0px, calc(var(--sl, 0px) - ${l}px), ${Math.max(0, w - 12 - Math.min(text.length * 6.6 + 20, 260) - (showDays ? 40 : 0))}px))` } : undefined}
                                      >
                                        {w > 46 && <span className="truncate">{text}</span>}
                                      </span>
                                    )}
                                    {showDays && <span className="ml-auto shrink-0 pr-2 text-[10.5px] tabular opacity-60">{days} d</span>}
                                    {draggable && w > 30 && <span data-handle="end" className="absolute right-0 top-0 h-full w-2 cursor-ew-resize" />}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs max-w-xs">
                                  <div className="font-medium">{bar.label}</div>
                                  <div>{label(bar.type)} · {label(bar.status)}</div>
                                  <div className="tabular">{fmtDate(bar.start)} – {fmtDate(bar.end)} · {Math.max(1, dayDiff(bar.end, bar.start))} d</div>
                                  {bar.module_code && <div className="font-mono">{bar.module_code}{bar.type_name ? ` · ${bar.type_name}` : ""}</div>}
                                  {bar.project_number && <div className="font-mono">{bar.project_number}</div>}
                                  {bar.conflict && <div className="text-rose-300 mt-1">Dobbelbooking</div>}
                                </TooltipContent>
                              </Tooltip>
                              {dragging && (
                                <div className="absolute z-40 -translate-y-full whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-medium text-background shadow-pop tabular pointer-events-none" style={{ left: l, top: top - 4 }} data-testid="planning-drag-label">
                                  {fmtDate(s, "d. MMM")} – {fmtDate(en, "d. MMM")} · {days} d
                                  {drag.mode === "move" && (() => { const dd = dayDiff(s, bar.start); return dd ? <span className="ml-1 opacity-80">({dd > 0 ? "+" : ""}{dd} d)</span> : null; })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {creating && (() => {
                          const a = Math.min(create.x0, create.x1);
                          const b = Math.max(create.x0, create.x1);
                          const s = dateAtX(a, "floor");
                          const en = dateAtX(b, "ceil");
                          const cw = clampX(x(en)) - clampX(x(s));
                          return (
                            <div className="absolute rounded-md border border-dashed border-primary/70 bg-primary/10 pointer-events-none z-30 flex items-center px-2 text-[11px] font-medium text-primary" style={{ left: clampX(x(s)), width: Math.max(6, cw), top: (base - barH) / 2, height: barH }} data-testid="planning-create-ghost">
                              {cw > 80 && <span className="truncate">Ny reservasjon · {fmtDate(s, "d. MMM")} – {fmtDate(en, "d. MMM")}</span>}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* filler keeps the left column opaque below the last row */}
            <div className="flex flex-1" aria-hidden>
              <div className="sticky left-0 z-20 shrink-0 border-r bg-card" style={{ width: leftWidth }} />
            </div>

            {/* today line */}
            {todayX >= 0 && todayX <= width && <div className="absolute inset-y-0 z-10 w-px bg-primary/70 pointer-events-none" style={{ left: leftWidth + todayX }} data-testid="planning-today-line" aria-hidden />}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}));
